import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useMediaQuery } from '../../../hooks';
import { styled } from '../../utils';
import { CardRow, CardTwoColumnRowSkeleton } from '../Card';
import { Collapsable } from '../Collapsable';
import { FormGrid } from '../FormGrid';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { Skeleton } from '../Skeleton';
import { Typography } from '../Typography';
const ColumnDiv = styled('div')({
    width: '100%',
});
// TODO: Add example of this component to styleguide
const TableRowDiv = styled('div', {
    shouldForwardProp: (prop) => prop !== 'noRowPadding' && prop !== 'striped' && prop !== 'filled',
})(({ theme, noRowPadding, filled, striped }) => ({
    verticalAlign: 'top',
    padding: theme.spacing(noRowPadding ? 0 : 1),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(noRowPadding ? 0 : 1),
    },
    ...(striped !== false && {
        '&:nth-of-type(odd)': {
            backgroundColor: `${theme.palette.primary.lighter}0D`,
        },
    }),
    ...(filled !== false && {
        backgroundColor: theme.palette.groupPrimary.aliceBlue,
    }),
}));
function SummaryTableRows(props) {
    const { rows, noContentMessage, striped, filled, noRowPadding, alignItems, flexWrap, flex, columns, } = props;
    const size = columns ? Math.ceil(rows.length / columns) : 1;
    const rowsSplit = columns
        ? Array.from({
            length: columns,
        }, (_, i) => i).reduce((acc, cur) => {
            const row = rows.slice(cur * size, cur * size + size);
            acc.push(row);
            return acc;
        }, [])
        : [rows];
    return (_jsx(FormGrid, { columnGap: 8, rowGap: 0, minWidth: "350px", children: noContentMessage && rows.length <= 0 ? (_jsx(Typography, { variant: "body1", component: "span", children: noContentMessage })) : (rowsSplit.map((splitRow, i) => (_jsx(ColumnDiv, { children: splitRow.map((row, i) => (_jsx(TableRowDiv, { noRowPadding: noRowPadding, filled: filled, striped: striped, children: _jsx(CardRow, { alignItems: alignItems, flexWrap: flexWrap, flex: flex, ...row }) }, i))) }, i)))) }));
}
const RootDiv = styled('div')(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(1),
}));
const TitleContainerDiv = styled('div', {
    shouldForwardProp: (prop) => prop !== 'alignItems' && prop !== 'expandable',
})(({ alignItems, expandable }) => ({
    display: 'flex',
    alignItems: alignItems || 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    '& .Typography-root': {
        alignItems: 'center',
    },
    '& .MuiCardHeader-root': {
        flex: 1,
    },
    ...(expandable && {
        cursor: 'pointer',
    }),
}));
const TitleTypographyStyled = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing(0.5),
    color: theme.palette.primary.dark,
}));
const IconStyled = styled(Icon)({
    position: 'relative',
    left: '-5px',
    cursor: 'pointer',
});
export function SummaryTable(props) {
    const { rows, noContentMessage, title, nesteds, noRowPadding, columns, striped, filled = false, alignItems = 'center', flexWrap = 'wrap', flex, expandable = false, initiallyExpanded = true, expandableFromBreakpointDown = 'sm', editable, onEdit, deletable, onDelete, } = props;
    const [expanded, setExpanded] = useState(initiallyExpanded);
    const match = useMediaQuery((theme) => theme.breakpoints.down(expandableFromBreakpointDown));
    const expandableSmallScreen = expandable === 'small-screens' && match;
    const reverseExpanded = () => {
        if (expandable === true || expandableSmallScreen) {
            setExpanded(!expanded);
        }
    };
    useEffect(() => {
        if (!expandableSmallScreen && initiallyExpanded) {
            setExpanded(true);
        }
    }, [expandableSmallScreen, initiallyExpanded]);
    return (_jsxs(RootDiv, { children: [(title || editable) && (_jsxs(TitleContainerDiv, { alignItems: alignItems, expandable: expandable === true || expandableSmallScreen, children: [title && (_jsxs(TitleTypographyStyled, { variant: "subtitle1", onClick: reverseExpanded, children: [(expandable === true || expandableSmallScreen) && (_jsx(IconStyled, { variant: expanded ? 'arrow-down-2' : 'arrow-right-2', color: "inherit", fontSize: "small" })), title] })), (editable || deletable) && (_jsxs("div", { children: [editable && (_jsx(IconButton, { onClick: onEdit, size: "small", color: "primary", variant: "pencil" })), deletable && (_jsx(IconButton, { onClick: onDelete, size: "small", color: "error", variant: "bin" }))] }))] })), expanded ? (_jsxs(_Fragment, { children: [_jsx(SummaryTableRows, { rows: rows, noContentMessage: noContentMessage, noRowPadding: noRowPadding, striped: striped, filled: filled, alignItems: alignItems, columns: columns, flexWrap: flexWrap, flex: flex }), !!nesteds && _jsx(Collapsable, { ...nesteds })] })) : null] }));
}
export function SummaryTableSkeleton({ count, title, }) {
    return (_jsx(SummaryTable, { title: title ? (_jsx(Skeleton, { height: 25, children: _jsx("span", { children: title }) })) : undefined, rows: Array.from(Array(count).keys()).map((_) => ({
            label: _jsx(CardTwoColumnRowSkeleton, {}),
            value: _jsx(CardTwoColumnRowSkeleton, {}),
        })) }));
}
